<template>
    <Form ref="form" :rules="validationRules" :model="formData" class="campaign-form" @submit.prevent="submit">
        <FormItem label="Campaign name" prop="name">
            <Input v-model="formData.name" data-testid="create-campaign__name" />
        </FormItem>

        <FormItem ref="campaignType" label="Template Type" prop="campaignType">
            <Select
                v-model="formData.campaignType"
                data-testid="create-campaign__campaign-type"
                class="open-media-studio-channel-select"
                :disabled="disableCampaignType"
            >
                <Option v-for="type in TemplateType" :key="type" :value="type" :label="type">
                    <component :is="CampaignTypeIcon[type]" /> {{ type }}
                </Option>
            </Select>
            <span v-if="disableCampaignType" class="campaign-form__helper-text">
                Template type cannot be changed once templates have been added
            </span>
        </FormItem>

        <FormItem label="WPP Project ID" prop="wppProjectId">
            <Input
                v-model="formData.wppProjectId"
                :disabled="disableWppProjectId"
                data-testid="create-campaign__wpp-project-id"
            />
        </FormItem>

        <FormItem v-if="canSeeOMSChannelControls" ref="OMSChannel" label="Open Media Studio Channel" prop="OMSChannel">
            <Select
                v-model="formData.opendcPlatformSettings!.campaignType"
                data-testid="create-campaign__oms-channel"
                class="open-media-studio-channel-select"
                :disabled="hasPlatformMetadata"
                @onChange="handleOMSChannelChange"
            >
                <template #prefix>
                    <component
                        :is="OMSIcon[formData.opendcPlatformSettings.campaignType]"
                        v-if="formData.opendcPlatformSettings?.campaignType"
                    />
                </template>
                <Option v-for="type in OpenDcCampaignType" :key="type" :value="type">
                    <component :is="OMSIcon[type]" /> {{ OpenDcCampaignTypeLabel[type] }}
                </Option>
            </Select>
        </FormItem>

        <FormItem label="Jira link" prop="jiraTicketUrl">
            <Input v-model="formData.jiraTicketUrl" data-testid="create-campaign__jira-ticket" />
        </FormItem>

        <FormItem label="Section" prop="sectionId">
            <SectionsSelect v-model="formData.sectionId" :sections="sections" />
        </FormItem>

        <FormItem v-if="uploadConfig" label="Campaign image" prop="mastheadImageUrl">
            <ImageUpload
                :model-value="formData.mastheadImageUrl"
                data-testid="edit-campaign__image"
                :config="uploadConfig"
                @imageUploaded="setCampaignImage"
            />
        </FormItem>

        <FormItem label="Tone of voice" prop="toneOfVoice">
            <Input ref="toneOfVoice" v-model="formData.toneOfVoice" type="textarea" />
        </FormItem>

        <FormItem label="Default timeline duration (in seconds)" prop="defaultTimelineLength">
            <Input
                v-model="formData.defaultTimelineLength"
                type="number"
                data-testid="create-campaign__timeline"
                :placeholder="BannerDefaultDuration"
            />
        </FormItem>
        <div v-if="hasCreativeXEnabled && formData.creativeXPlatformSettings" class="creative-x-settings">
            <h3>Creative X Settings</h3>

            <hox-loading-layer v-if="isLoadingCreativeX" :is-full-screen="false" />
            <div v-else>
                <CreativeXError v-if="creativeXHasError" @refetch="fetchCreativeXDimensions" />

                <FormItem label="Market code" prop="creativeXPlatformSettings.marketCode">
                    <Select
                        v-model="formData.creativeXPlatformSettings.marketCode"
                        filterable
                        data-testid="create-campaign__market-code"
                    >
                        <Option v-for="option in creativeXOptions?.markets" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Channel" prop="creativeXPlatformSettings.channel">
                    <Select
                        v-model="formData.creativeXPlatformSettings.channel"
                        data-testid="create-campaign__channel"
                        @onChange="handleChannelChange"
                    >
                        <Option v-for="option in creativeXOptions?.channels" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Publisher" prop="creativeXPlatformSettings.publisher">
                    <Select
                        v-model="formData.creativeXPlatformSettings.publisher"
                        :placeholder="
                            formData?.creativeXPlatformSettings?.channel ? 'Select' : 'Please select the Channel'
                        "
                        data-testid="create-campaign__publisher"
                        :disabled="!formData?.creativeXPlatformSettings?.channel"
                    >
                        <Option
                            v-for="option in creativeXAdPublishersOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Placement" prop="creativeXPlatformSettings.placement">
                    <Select
                        v-model="formData.creativeXPlatformSettings.placement"
                        :placeholder="
                            formData?.creativeXPlatformSettings?.publisher ? 'Select' : 'Please select the Publisher'
                        "
                        data-testid="create-campaign__placement"
                        :disabled="!formData?.creativeXPlatformSettings?.publisher"
                    >
                        <Option
                            v-for="option in creativeXAdPlacementsOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Campaign objective" prop="creativeXPlatformSettings.campaignObjective">
                    <Select
                        v-model="formData.creativeXPlatformSettings.campaignObjective"
                        :placeholder="
                            formData?.creativeXPlatformSettings?.channel ? 'Select' : 'Please select the Channel'
                        "
                        data-testid="create-campaign__campaign-objective"
                    >
                        <Option
                            v-for="option in creativeXCampaignObjectivesOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem ref="contentType" label="Content type" prop="creativeXPlatformSettings.contentType">
                    <Select
                        v-model="formData.creativeXPlatformSettings.contentType"
                        data-testid="edit-campaign__content-type"
                    >
                        <Option v-for="(value, label) in CreativeXCampaignType" :key="value" :value="value">
                            {{ label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Ad format" prop="creativeXPlatformSettings.adFormat">
                    <Select
                        v-model="formData.creativeXPlatformSettings.adFormat"
                        :placeholder="
                            formData?.creativeXPlatformSettings?.channel ? 'Select' : 'Please select the Channel'
                        "
                        data-testid="create-campaign__ad-format"
                        :disabled="!formData?.creativeXPlatformSettings?.channel"
                    >
                        <Option v-for="option in creativeXAdFormatsOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Asset type" prop="creativeXPlatformSettings.assetType">
                    <Select
                        v-model="formData.creativeXPlatformSettings.assetType"
                        data-testid="edit-campaign__asset-type"
                    >
                        <Option v-for="(value, label) in CreativeXAssetType" :key="value" :value="value">
                            {{ label }}
                        </Option>
                    </Select>
                </FormItem>
            </div>
        </div>
        <div v-if="hasSataliaEnabled && formData.sataliaPlatformSettings" class="satalia-settings">
            <h3>Pre-flight Settings</h3>
            <div class="preflight-settings-grid">
                <FormItem label="Asset type" prop="sataliaPlatformSettings.assetType">
                    <Select
                        v-model="formData.sataliaPlatformSettings.assetType"
                        data-testid="edit-campaign__satalia-asset-type"
                    >
                        <Option value="IMAGE">Image</Option>
                        <Option value="VIDEO">Video</Option>
                    </Select>
                </FormItem>
            </div>
        </div>
    </Form>
</template>

<script lang="ts">
import { defineComponent, markRaw, type PropType } from "vue";
import type { RuleItem } from "async-validator";
import type { Form } from "view-ui-plus";

import type { TemplateType as TemplateTypeType } from "hoxton-types";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { USER_ACTIONS } from "hoxton-shared/enums/user";

import type { MastheadUploadConfig } from "@/types/types";
import type { CreativeXChannel, CreativeXCampaignObjectiveRequest } from "@/types/preflight/creativeX";
import { OpenDcCampaignType } from "hoxton-shared/enums/opendc";

import ImageUpload from "@/components/ImageUpload.vue";
import SectionsSelect from "@/components/SectionsSelect.vue";
import CreativeXError from "@/components/CreativeXError.vue";

import { BannerDefaultDuration } from "@/enums/banners";
import { CreativeXCampaignType, CreativeXAssetType, OpenDcCampaignTypeLabel } from "@/enums/platforms";
import { getCreativeXDimensions } from "@/utils/CreativeIntelligenceApi/creativex";
import formatCreativeXDimensions from "@/utils/CreativeX/formatCreativeXDimensions";
import getDefaultCreativeXCampaignSettings from "@/utils/CreativeX/getDefaultCreativeXCampaignSettings";
import getDefaultSataliaCampaignSettings from "@/utils/Satalia/getDefaultSataliaCampaignSettings";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

import DisplayIcon from "@/assets/open-media-studio-icons/display.svg";
import VideoIcon from "@/assets/open-media-studio-icons/video.svg";
import MetaIcon from "@/assets/open-media-studio-icons/meta.svg";
import TikTokIcon from "@/assets/open-media-studio-icons/tiktok.svg";

import HTML5Icon from "@/assets/html5_icon.svg";
import PhotoshopIcon from "@/assets/photoshop_icon.svg";
import AEIcon from "@/assets/ae_icon.svg";
import InDesignIcon from "@/assets/Adobe_Indesign_CC_2026_icon.svg";
import { userCan } from "@/services/Auth";

const OMSIcon = markRaw({
    [OpenDcCampaignType.display]: DisplayIcon,
    [OpenDcCampaignType.video]: VideoIcon,
    [OpenDcCampaignType.socialStatic]: MetaIcon,
    [OpenDcCampaignType.socialVideo]: MetaIcon,
    [OpenDcCampaignType.tiktokVideo]: TikTokIcon,
    [OpenDcCampaignType.tiktokStatic]: TikTokIcon
});

const CampaignTypeIcon = markRaw({
    [TemplateType.AE]: AEIcon,
    [TemplateType.HTML5]: HTML5Icon,
    [TemplateType.PSD]: PhotoshopIcon,
    [TemplateType.INDESIGN]: InDesignIcon
});

const urlRegex = /http(s)?:\/\/[a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/;
const nameWhitelistRegex = /^[a-zA-Z0-9_\s\-<>]+$/;
const projectIdRegex = /^[a-zA-Z0-9-_]+$/;

const validationRules = {
    "creativeXPlatformSettings.marketCode": [
        { required: true, message: "Please provide a valid Market Code for CreativeX.", trigger: "change" }
    ],
    "creativeXPlatformSettings.channel": [
        { required: true, message: "Please provide a valid Channel for CreativeX.", trigger: "change" }
    ],
    name: [
        { required: true, message: "A campaign requires a name", whitespace: true },
        {
            pattern: nameWhitelistRegex,
            message: "A campaign name can only contain numbers, letters, spaces and following characters: -, _, <, >"
        }
    ],
    campaignType: [{ required: true, message: "Please provide a valid Template Type.", trigger: "change" }],
    wppProjectId: [
        {
            pattern: projectIdRegex,
            message: "A WPP Project ID can only contain numbers, letters and following characters: -, _"
        }
    ],
    jiraTicketUrl: [
        {
            pattern: urlRegex,
            message: "The url doesn't seem to be valid. Example: https://example.com"
        }
    ],
    defaultTimelineLength: [
        {
            type: "number",
            message: "The value needs to be positive",
            validator: (_: RuleItem, value: string | undefined | null) => {
                if (value === undefined || value === "" || value === null) {
                    return true;
                }

                return parseInt(value, 10) >= 0;
            }
        }
    ]
};

export type CampaignFormData = {
    name: string;
    campaignType: TemplateTypeType;
    jiraTicketUrl: string;
    sectionId: string;
    mastheadImageUrl: string;
    toneOfVoice: string;
    defaultTimelineLength: string;
    content: string;
    wppProjectId: string;
    opendcPlatformSettings: {
        campaignType?: (typeof OpenDcCampaignType)[keyof typeof OpenDcCampaignType];
    } | null;
    creativeXPlatformSettings?: {
        marketCode?: string;
        channel: CreativeXChannel | "";
        publisher?: string;
        placement?: string;
        campaignObjective?: CreativeXCampaignObjectiveRequest | "";
        contentType?: "BRAND" | "CREATOR";
        adFormat?: string;
        assetType?: "VIDEO" | "IMAGE";
    } | null;
    sataliaPlatformSettings?: {
        assetType?: "VIDEO" | "IMAGE";
    } | null;
};

export default defineComponent({
    name: "CampaignForm",

    components: {
        ImageUpload,
        SectionsSelect,
        CreativeXError
    },

    props: {
        initialFormData: {
            type: Object as PropType<Partial<CampaignFormData>>
        },

        sections: {
            required: true,
            type: Array as PropType<{ _id: string; name: string }[]>,
            validator(value: { _id: string; name: string }[]) {
                return value.every(item => item._id !== undefined && item.name !== undefined);
            }
        },

        uploadConfig: {
            type: Object as PropType<MastheadUploadConfig>
        },

        disableWppProjectId: {
            type: Boolean,
            default: false
        },

        hasCreativeXEnabled: {
            type: Boolean,
            default: false
        },

        clientCreativeXSecretId: {
            type: String,
            default: ""
        },

        hasSataliaEnabled: {
            type: Boolean,
            default: false
        },

        hasPlatformMetadata: {
            type: Boolean,
            default: false
        },

        disableCampaignType: {
            type: Boolean,
            default: false
        }
    },

    emits: ["hasValidationErrorsUpdate", "submit"],

    setup() {
        const ga4 = useGA4Tracking();
        return { ga4 };
    },

    data() {
        return {
            OMSIcon,
            OpenDcCampaignType,
            validationRules,
            CreativeXCampaignType,
            CreativeXAssetType,
            OpenDcCampaignTypeLabel,
            CampaignTypeIcon,
            TemplateType,
            formData: {} as Partial<CampaignFormData>,
            BannerDefaultDuration: BannerDefaultDuration.toString(),
            isLoadingCreativeX: false,
            creativeXOptions: null as ReturnType<typeof formatCreativeXDimensions>,
            creativeXHasError: false
        };
    },

    computed: {
        channel() {
            return this.formData?.creativeXPlatformSettings?.channel;
        },

        creativeXAdFormatsOptions() {
            if (!this.channel) {
                return [];
            }

            return this.creativeXOptions?.adFormats?.[this.channel] || [];
        },

        creativeXAdPublishersOptions() {
            if (!this.channel) {
                return [];
            }

            return this.creativeXOptions?.publishers?.[this.channel] || [];
        },

        creativeXCampaignObjectivesOptions() {
            if (!this.channel) {
                return [];
            }

            return this.creativeXOptions?.campaignObjectives?.[this.channel] || [];
        },

        creativeXAdPlacementsOptions() {
            if (!this.channel) {
                return [];
            }

            return (
                this.creativeXOptions?.publishers?.[this.channel]?.find(
                    publisher => publisher.value === this.formData.creativeXPlatformSettings?.publisher
                )?.placements || []
            );
        },

        connectModuleEnabled() {
            return this.$store.state.ui.currentClient?.connectModuleEnabled ?? false;
        },

        userCanSetCampaignOMSChannel() {
            return userCan(USER_ACTIONS.canSetCampaignOMSChannel);
        },

        canSeeOMSChannelControls() {
            return this.connectModuleEnabled && this.userCanSetCampaignOMSChannel;
        }
    },

    watch: {
        initialFormData: {
            deep: true,
            async handler(newData) {
                (this.$refs?.form as typeof Form)?.resetFields();

                const localData = JSON.parse(JSON.stringify(newData || {})) as Partial<CampaignFormData>;

                if (this.hasCreativeXEnabled && !localData.creativeXPlatformSettings) {
                    localData.creativeXPlatformSettings = getDefaultCreativeXCampaignSettings();
                }

                if (this.hasSataliaEnabled && !localData.sataliaPlatformSettings) {
                    localData.sataliaPlatformSettings = getDefaultSataliaCampaignSettings();
                }

                this.formData = {
                    ...localData,
                    mastheadImageUrl: localData.mastheadImageUrl || "",
                    sectionId: localData?.sectionId || "none",
                    opendcPlatformSettings: {
                        campaignType: localData.opendcPlatformSettings?.campaignType
                    }
                };
            },
            immediate: true
        },

        formData: {
            deep: true,
            async handler() {
                const isValid = await (this.$refs?.form as typeof Form)?.validate();

                this.$emit("hasValidationErrorsUpdate", !isValid);
            }
        }
    },

    mounted() {
        if (this.hasCreativeXEnabled) {
            this.fetchCreativeXDimensions();
        }
    },

    methods: {
        setCampaignImage(imagePath: string) {
            this.formData.content = imagePath;
        },

        async submit() {
            const isValid = await (this.$refs?.form as typeof Form)?.validate();

            if (!isValid) {
                this.$emit("hasValidationErrorsUpdate", !isValid);

                return;
            }

            this.$emit("submit", { ...this.formData });
        },

        handleChannelChange() {
            if (this.formData.creativeXPlatformSettings) {
                this.formData.creativeXPlatformSettings.adFormat = undefined;
                this.formData.creativeXPlatformSettings.placement = undefined;
                this.formData.creativeXPlatformSettings.publisher = undefined;
                this.formData.creativeXPlatformSettings.campaignObjective = undefined;
            }
        },

        handleOMSChannelChange(type: string) {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                interactionText: `${GA4.CLICK_INTERACTION_TEXTS.OMS_CHANNEL_SELECTED}: ${OpenDcCampaignTypeLabel[type as keyof typeof OpenDcCampaignTypeLabel]}`
            });
        },

        async fetchCreativeXDimensions() {
            this.creativeXHasError = false;
            this.isLoadingCreativeX = true;

            try {
                const { data } = await getCreativeXDimensions(this.clientCreativeXSecretId);

                this.creativeXOptions = formatCreativeXDimensions(data);
            } catch {
                this.creativeXHasError = true;
                this.$snackbar.error("Failed to fetch CreativeX dimensions");
            } finally {
                this.isLoadingCreativeX = false;
            }
        }
    }
});
</script>

<style scoped lang="scss">
.campaign-form {
    max-height: 70dvh;
    padding-right: 20px;
    overflow: auto;

    h3 {
        margin: 30px 0 10px;
        border-bottom: 1px solid var(--wpp-grey-color-400);
    }
}
.open-media-studio-channel-select {
    li {
        display: flex;
        align-items: center;
        gap: 10px;

        svg {
            width: 20px;
            height: 20px;
        }
    }
    :global(.ivu-select-head-with-prefix) {
        margin-left: 5px;
    }
}
.campaign-form__helper-text {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: var(--wpp-grey-color-600);
}
</style>
